// React imports
import React, { Component } from "react";
import { propTypes, reduxForm, Field } from "redux-form";
import { connect } from "react-redux";

// Other imports
import PropTypes from "prop-types";
import compose from "recompose/compose";

// Material Ui imports
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { withStyles } from "@material-ui/core/styles";
import LockIcon from "@material-ui/icons/Lock";
import PersonIcon from "@material-ui/icons/Person";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

// React Admin imports
import { Notification, translate, userLogin } from "react-admin";

// Style imports
import LoginStyles from "./theme/styles/LoginStyles";
import LibraryTranslations from "./i18n/LibraryTranslations";

const renderInput = ({
  meta: { touched, error } = {},
  input,
  classes,
  icon: Icon,
  label,
  trailing,
  ...props
}) => {
  const hasError = !!(touched && error);

  return (
    <div className={classes.fieldWrap}>
      <label className={classes.label} htmlFor={input.name}>
        {label}
      </label>
      <div
        className={
          hasError ? `${classes.field} ${classes.fieldError}` : classes.field
        }
      >
        <Icon className={classes.fieldIcon} />
        <input
          id={input.name}
          className={classes.input}
          {...input}
          {...props}
        />
        {trailing}
      </div>
      {hasError && <div className={classes.errorText}>{error}</div>}
    </div>
  );
};

class Login extends Component {
  state = { passwordVisible: false };

  togglePassword = () =>
    this.setState(({ passwordVisible }) => ({
      passwordVisible: !passwordVisible
    }));

  login = auth =>
    this.props.userLogin(
      auth,
      this.props.location.state ? this.props.location.state.nextPathname : "/"
    );

  render() {
    const { classes, handleSubmit, isLoading, translate } = this.props;
    const { passwordVisible } = this.state;
    const VisibilityToggleIcon = passwordVisible
      ? VisibilityOffIcon
      : VisibilityIcon;

    return (
      <div className={classes.main}>
        <div className={classes.card}>
          <form onSubmit={handleSubmit(this.login)}>
            <img className={classes.logo} src="/images/logo.png" alt="Klima team" />
            <h1 className={classes.title}>{translate("ra.auth.sign_in")}</h1>
            <p className={classes.subtitle}>
              {translate("ra.auth.sign_in_subtitle")}
            </p>
            <Field
              name="username"
              component={renderInput}
              classes={classes}
              icon={PersonIcon}
              label={translate("ra.auth.username")}
              placeholder={translate("ra.auth.username_placeholder")}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              spellCheck={false}
              disabled={isLoading}
            />
            <Field
              name="password"
              component={renderInput}
              classes={classes}
              icon={LockIcon}
              label={translate("ra.auth.password")}
              placeholder={translate("ra.auth.password_placeholder")}
              type={passwordVisible ? "text" : "password"}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="current-password"
              spellCheck={false}
              disabled={isLoading}
              trailing={
                <button
                  type="button"
                  className={classes.visibilityButton}
                  onClick={this.togglePassword}
                  aria-label={translate(
                    passwordVisible
                      ? "ra.auth.hide_password"
                      : "ra.auth.show_password"
                  )}
                >
                  <VisibilityToggleIcon />
                </button>
              }
            />
            <Button
              variant="raised"
              type="submit"
              disabled={isLoading}
              className={classes.button}
              fullWidth
            >
              {isLoading ? (
                <CircularProgress size={25} thickness={3} color="inherit" />
              ) : (
                translate("ra.auth.sign_in_submit")
              )}
            </Button>
          </form>
        </div>
        <LibraryTranslations>
          <Notification />
        </LibraryTranslations>
      </div>
    );
  }
}

Login.propTypes = {
  ...propTypes,
  classes: PropTypes.object,
  translate: PropTypes.func.isRequired,
  userLogin: PropTypes.func.isRequired
};

const mapStateToProps = state => ({ isLoading: state.admin.loading > 0 });

const enhance = compose(
  translate,
  reduxForm({
    form: "signIn",
    validate: (values, props) => {
      const errors = {};
      const { translate } = props;
      if (!values.username) {
        errors.username = translate("ra.validation.required");
      }
      if (!values.password) {
        errors.password = translate("ra.validation.required");
      }
      return errors;
    }
  }),
  connect(
    mapStateToProps,
    { userLogin }
  ),
  withStyles(LoginStyles)
);

export default enhance(Login);
